import { useRef, useState, useEffect } from 'preact/hooks';
import styles from '../../styles/ProblemComparison.module.css';
import commerceAlertImg from '../../assets/images/commerce_alert.png';
import olympAlertImg from '../../assets/images/olym_commerce_style_push.png';

const shoppingMallAlerts = [
  { target: '장바구니에 담고 결제 안한 고객', action: '상품 구매, 잊으신 건 아닌가요?' },
  { target: '구매한지 30일 지난 고객', action: '재구매 10% 할인 쿠폰 발급!' },
  { target: '재고 안 빠지는 상품', action: '타임 세일 지금부터 딱 1시간!' },
  { target: '첫 구매 고객', action: '첫 구매 고객 할인 이벤트' },
  { target: '단골 고객', action: 'VIP 고객님만을 위한 특별 혜택' }
];

const olympAlerts = [
  { target: '체험 레슨 후 등록 안한 고객', action: '등록 고민 중이세요? 정기 레슨 등록시 10% 할인 쿠폰' },
  { target: '레슨권 만료가 얼마 남지 않은 고객', action: '레슨이 곧 만료돼요. 재등록 쿠폰 발급!' },
  { target: '평일 오전 빈 시간대 대관', action: '올림 테니스 센터 타임세일! 평일 새벽 시간대 대폭 할인' },
  { target: '신규 가입 고객', action: '이번 주말, 테니스 어떠세요? 원데이 레슨 할인권 도착!' },
  { target: '단골 고객', action: '단골 고객님을 위한 특별 프로모션 도착!' }
];

function useScrollProgress(containerRef) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // 섹션이 화면에 보이기 시작하면 0, 화면 중앙쯤 오면 1
      const scrollProgress = Math.max(0, Math.min(1,
        (viewportHeight * 0.3 - rect.top) / (viewportHeight * 0.5)
      ));

      // 5개 알림 중 어떤 것을 보여줄지 계산
      const newIndex = Math.min(4, Math.floor(scrollProgress * 5));
      setActiveIndex(newIndex);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 초기 실행

    return () => window.removeEventListener('scroll', handleScroll);
  }, [containerRef]);

  return activeIndex;
}

export default function ProblemComparison() {
  const containerRef1 = useRef(null);
  const containerRef2 = useRef(null);
  const activeIndex1 = useScrollProgress(containerRef1);
  const activeIndex2 = useScrollProgress(containerRef2);

  return (
    <>
      {/* 섹션 1: 쇼핑몰은 이미 이렇게 합니다 */}
      <section id="problem" ref={containerRef1} className={`section ${styles.problem} ${styles.scrollSection}`}>
        <div className="section-content">
          <h2 className={`text-center ${styles.title}`}>
            쇼핑몰은 이미 이렇게 합니다
          </h2>
          <p className={`text-center ${styles.subtitle}`}>
            담당자가 일일이 하지 않아도, 시스템이 알아서 고객을 관리합니다
          </p>

          <div className={styles.scrollAlertSection}>
            <div className={styles.alertListSticky}>
              {shoppingMallAlerts.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.scrollAlertItem} ${index <= activeIndex1 ? styles.activeAlert : ''}`}
                >
                  <span className={styles.alertTarget}>{item.target}</span>
                  <span className={styles.alertArrow}>→</span>
                  <span className={styles.alertAction}>{item.action}</span>
                </div>
              ))}
            </div>
            <div className={styles.phoneImageSticky}>
              <img src={commerceAlertImg} alt="쇼핑몰 자동 알림 예시" />
            </div>
          </div>

          <div className={`${styles.bottomMessage} ${activeIndex1 >= 4 ? styles.visible : ''}`}>
            <p className={styles.shoppingConclusion}>
              온라인 커머스 업계에서는 이미 표준입니다.
              <br />
              <strong>직원이 문자 보내는 게 아니라 시스템이 보냅니다.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* 섹션 2: 스포츠 센터도 똑같이 할 수 있습니다 */}
      <section ref={containerRef2} className={`section ${styles.problem} ${styles.scrollSection}`}>
        <div className="section-content">
          <h2 className={`text-center ${styles.title}`}>
            스포츠 센터도 똑같이 할 수 있습니다
          </h2>
          <p className={`text-center ${styles.subtitle}`}>
            올림이 쇼핑몰의 자동화 전략을 스포츠 센터에 적용했습니다
          </p>

          <div className={`${styles.scrollAlertSection} ${styles.olympAlerts}`}>
            <div className={styles.alertListSticky}>
              {olympAlerts.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.scrollAlertItem} ${index <= activeIndex2 ? styles.activeAlert : ''}`}
                >
                  <span className={styles.alertTarget}>{item.target}</span>
                  <span className={styles.alertArrow}>→</span>
                  <span className={styles.alertAction}>{item.action}</span>
                </div>
              ))}
            </div>
            <div className={styles.phoneImageSticky}>
              <img src={olympAlertImg} alt="올림 자동 알림 예시" />
            </div>
          </div>

          <div className={`${styles.bottomMessage} ${activeIndex2 >= 4 ? styles.visible : ''}`}>
            <p className={styles.olympConclusion}>
              쇼핑몰의 검증된 매출 성장 전략을 스포츠 센터에도 그대로 적용
              <br />
              <strong>올림과 함께라면 가능합니다</strong>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
